import React from 'react';
import { css } from '@emotion/css';

const policyPage = css({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-lg)',
  minHeight: 'calc(100vh - 100px)',
});

const policyPageContainer = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const policyPageHeader = css({
  marginBottom: 'var(--spacing-lg)',
  borderBottom: '2px solid var(--pc-200)',
  paddingBottom: 'var(--spacing-md)',
});

const policyPageTitle = css({
  fontSize: 'var(--fs-800)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-xs)',
});

const policyPageSubtitle = css({
  fontSize: 'var(--fs-500)',
  color: 'var(--g-700)',
  fontWeight: 400,
});

const policyPageContentMeia = css({
  fontSize: 'var(--fs-400)',
  lineHeight: 1.6,
  color: 'var(--g-800)',
  marginTop: '-3rem',
});

const policyPageSection = css({
  marginBottom: 'var(--spacing-lg)',
});

const policyPageSectionTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-sm)',
  paddingBottom: 'var(--spacing-xs)',
  borderBottom: '1px solid var(--pc-100)',
});

const policyPageSectionContent = css({
  marginBottom: 'var(--spacing-md)',
});

const policyPageSectionSubtitle = css({
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  margin: 'var(--spacing-md) 0 var(--spacing-xs)',
});

const policyPageList = css({
  listStyleType: 'disc',
  paddingLeft: 'var(--spacing-lg)',
  marginBottom: 'var(--spacing-md)',
});

const policyPageListItem = css({
  marginBottom: 'var(--spacing-xs)',
});

const policyPageAttention = css({
  backgroundColor: 'rgba(var(--pcrgba-50), 0.5)',
  borderLeft: '4px solid var(--pc-500)',
  padding: 'var(--spacing-md)',
  margin: 'var(--spacing-md) 0',
  borderRadius: 'var(--spacing-xs)',
});

const policyPageAttentionTitle = css({
  fontWeight: 600,
  marginBottom: 'var(--spacing-xs)',
  color: 'var(--pc-700)',
});

// Media queries for responsiveness
const responsiveStyles = css({
  '@media (max-width: 768px)': {
    [`&.${policyPage}`]: {
      padding: 'var(--spacing-md)',
    },
    [`.${policyPageTitle}`]: {
      fontSize: 'var(--fs-700)',
    },
    [`.${policyPageSubtitle}`]: {
      fontSize: 'var(--fs-450)',
    },
    [`.${policyPageSectionTitle}`]: {
      fontSize: 'var(--fs-500)',
    },
  },
  '@media (max-width: 480px)': {
    [`&.${policyPage}`]: {
      padding: 'var(--spacing-sm)',
    },
    [`.${policyPageTitle}`]: {
      fontSize: 'var(--fs-600)',
    },
    [`.${policyPageSubtitle}`]: {
      fontSize: 'var(--fs-400)',
    },
    [`.${policyPageContentMeia}`]: {
      fontSize: 'var(--fs-350)',
    },
    [`.${policyPageSectionTitle}`]: {
      fontSize: 'var(--fs-450)',
    },
  },
});

export const HalfPricePolicy: React.FC = () => {
  return (
    <div className={`${policyPage} ${responsiveStyles}`}>
      <div className={policyPageContainer}>
        <div className={policyPageHeader}>
          <h1 className={policyPageTitle}>Políticas de Meia-entrada</h1>
        </div>

        <div className={policyPageContentMeia}>
          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Condições para validação do Benefício de Meia-entrada</h3>
            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p>
                A meia-entrada é válida mediante apresentação de documento comprobatório no momento da entrada. 
                A não apresentação implicará no pagamento da diferença do valor integral ou impedimento de acesso 
                ao evento.
              </p>
            </div>
            <div className={policyPageSectionContent}>
              <p>
                O benefício da meia-entrada segue critérios definidos por lei. Consulte abaixo os requisitos e 
                os beneficiários da meia-entrada, conforme a legislação brasileira e as normas específicas de 
                cada estado.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Lei de meia-entrada válida para todo o país</h3>
            <div className={policyPageSectionContent}>
              <p>
                De acordo com a Lei Federal 12.852 (Estatuto da Juventude) e 12.933 de 2013, o estudante, jovem 
                de baixa renda e pessoa com deficiência (e um acompanhante) têm direito ao benefício da meia-entrada, 
                limitada a 40% do total de ingressos disponíveis para cada evento, nos eventos expressamente previstos 
                em Lei:
              </p>
              <p style={{ marginTop: 'var(--spacing-md)', fontStyle: 'italic' }}>
                Art. 1º É assegurado aos estudantes o acesso a salas de cinema, cineclubes, teatros, espetáculos 
                musicais e circenses e eventos educativos, esportivos, de lazer e de entretenimento, em todo o 
                território nacional, promovidos por quaisquer entidades e realizados em estabelecimentos públicos ou 
                particulares, mediante pagamento da metade do preço do ingresso efetivamente cobrado do público em geral.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Beneficiários</h3>
            
            <h4 className={policyPageSectionSubtitle}>I- Estudantes – alteração na documentação</h4>
            <div className={policyPageSectionContent}>
              <p>
                Apresentar a Carteira de Identificação Estudantil – CIE – documento que comprova a condição de 
                estudante regularmente matriculado nos níveis e modalidades de educação e ensino previstos no 
                Título V da Lei nº 9.394, de 1996, conforme modelo único nacionalmente padronizado, com certificação 
                digital e que deverá conter: nome completo, data de nascimento do estudante, foto recente, nome da 
                instituição de ensino, grau de escolaridade e data de validade, conforme Seção I do Decreto 8.537/2015. 
                Será exigida a apresentação da CIE no acesso ao evento. Nos termos do artigo 1-A, da Lei 12.933 de 2013, 
                a CIE pode ser emitida pelo Ministério da Educação, ANPG, UNE, Ubes, bem como pelas entidades estudantis 
                estaduais e municipais, pelos Diretórios Centrais dos Estudantes (DCE´s), pelos Centros e Diretórios 
                Acadêmicos e por outras entidades de ensino e associações representativas dos estudantes. Cabe ressaltar 
                que boleto bancário não é documentação hábil para comprovar a condição de estudante.
              </p>
            </div>

            <h4 className={policyPageSectionSubtitle}>II- Jovens de 15 a 29 anos pertencentes a famílias de baixa renda</h4>
            <div className={policyPageSectionContent}>
              <p>
                Válido a partir de 31 de março de 2016 inscritos no Cadastro Único para Programas Sociais do Governo 
                Federal – CadÚnico cuja renda mensal seja de até 2 (dois) salários mínimos.
              </p>
              <p style={{ marginTop: 'var(--spacing-sm)' }}>
                Os jovens de baixa renda terão direito ao benefício da meia-entrada mediante a apresentação, no momento 
                da aquisição do ingresso e na portaria ou na entrada do local de realização do evento, da Identidade 
                Jovem acompanhada de documento de identificação com foto expedido por órgão público e válido em todo o 
                território nacional.
              </p>
            </div>

            <h4 className={policyPageSectionSubtitle}>III- Deficiente e seu acompanhante</h4>
            <div className={policyPageSectionContent}>
              <p>
                (este, se necessário) Mediante apresentação de documento que comprove a condição, de acordo com a Lei 
                Federal 12.933 de 2013. Apenas um acompanhante, por pessoa com deficiência, terá direito ao benefício 
                da meia-entrada.
              </p>
              <p style={{ marginTop: 'var(--spacing-sm)' }}>
                De acordo com a Lei Federal nº 10.741/2003 (Estatuto do Idoso), os beneficiários mencionados abaixo 
                tem direito a compra de meia-entrada (conforme previsto na lei, este benefício não é limitado):
              </p>
            </div>

            <h4 className={policyPageSectionSubtitle}>IV- Idosos (com idade igual ou superior a 60 anos)</h4>
            <div className={policyPageSectionContent}>
              <p>
                De acordo com a Lei Federal nº 10.741/2003 (Estatuto do Idoso), os idosos têm direito ao benefício da 
                meia-entrada (conforme previsto na Lei, este benefício não é limitado)
              </p>
              <p style={{ marginTop: 'var(--spacing-sm)' }}>
                Apresentar documento de identidade oficial com foto que comprove a condição.
              </p>
            </div>

            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p style={{ marginBottom: 'var(--spacing-xs)' }}>
                Para Pontos de Venda e Bilheterias é necessária a comprovação do direito ao benefício da meia-entrada 
                no ato da compra e no acesso ao evento. Para vendas pela Internet e Telefone é necessária a comprovação 
                do direito ao benefício da meia-entrada no acesso ao evento.
              </p>
              <p>
                Caso o benefício não seja comprovado, o portador deverá complementar o valor do ingresso adquirido para 
                o valor do ingresso integral, caso contrário o acesso ao evento não será permitido.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>São Paulo</h3>
            <div className={policyPageSectionContent}>
              <p>Estudantes, jovens de 15 a 29 anos pertencentes a famílias de baixa renda, idoso e deficiente e seu acompanhante.</p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Diretor, Coordenador Pedagógico, Supervisor e titular do quadro de apoio de escolar Estadual e 
                  Municipal – Lei Estadual SP 15.298/14 (apresentar documento de identidade oficial com foto e 
                  carteira funcional da Secretaria de Educação ou Holerite que comprove a condição).
                </li>
                <li className={policyPageListItem}>
                  Professor da rede pública Estadual e Municipal – Lei Estadual SP 14.729/2012 (apresentar documento 
                  de identidade oficial com foto e carteira funcional da Secretaria de Educação ou Holerite que 
                  comprove a condição).
                </li>
              </ul>
              <p style={{ marginTop: 'var(--spacing-md)' }}><strong>Exclusivamente para o município de São Paulo:</strong></p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Aposentados – Lei Municipal SP nº 12.325/1997 (apresentar documento de identidade oficial com foto 
                  e cartão de benefício do INSS que comprove a condição).
                </li>
              </ul>
            </div>
            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p style={{ marginBottom: 'var(--spacing-xs)' }}>
                Para Pontos de Venda e Bilheterias é necessária a comprovação do direito ao benefício da meia-entrada 
                no ato da compra e no acesso ao evento. Para vendas pela Internet e Telefone é necessária a comprovação 
                do direito ao benefício da meia-entrada no acesso ao evento.
              </p>
              <p>
                Caso o benefício não seja comprovado, o portador deverá complementar o valor do ingresso adquirido para 
                o valor do ingresso integral, caso contrário o acesso ao evento não será permitido.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Rio de Janeiro</h3>
            <div className={policyPageSectionContent}>
              <p>Estudantes, jovens de 15 a 29 anos pertencentes a famílias de baixa renda, idoso e deficiente e seu acompanhante.</p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Menores de 21 anos – Lei Estadual RJ nº 3.364/2000 (apresentar documento de identidade oficial com 
                  foto que comprove idade inferior a 21 anos).
                </li>
              </ul>
              <p style={{ marginTop: 'var(--spacing-md)' }}><strong>Exclusivamente para o município Rio de Janeiro:</strong></p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Professores e Profissionais da rede pública Municipal de ensino – Lei Municipal RJ nº 3.424/2002 
                  (apresentação da carteira funcional emitida pela Secretaria Municipal de Educação).
                </li>
              </ul>
            </div>
            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p style={{ marginBottom: 'var(--spacing-xs)' }}>
                Para Pontos de Venda e Bilheterias é necessária a comprovação do direito ao benefício da meia-entrada 
                no ato da compra e no acesso ao evento. Para vendas pela Internet e Telefone é necessária a comprovação 
                do direito ao benefício da meia-entrada no acesso ao evento.
              </p>
              <p>
                Caso o benefício não seja comprovado, o portador deverá complementar o valor do ingresso adquirido para 
                o valor do ingresso integral, caso contrário o acesso ao evento não será permitido.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Minas Gerais</h3>
            <div className={policyPageSectionContent}>
              <p>Estudantes, jovens de 15 a 29 anos pertencentes a famílias de baixa renda, idoso e deficiente e seu acompanhante.</p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Menores de 21 anos – Lei Municipal de Belo Horizonte nº 9.070/2005 (apresentar documento de 
                  identidade oficial com foto que comprove a condição).
                </li>
              </ul>
            </div>
            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p style={{ marginBottom: 'var(--spacing-xs)' }}>
                Para Pontos de Venda e Bilheterias é necessária a comprovação do direito ao benefício da meia-entrada 
                no ato da compra e no acesso ao evento. Para vendas pela Internet e Telefone é necessária a comprovação 
                do direito ao benefício da meia-entrada no acesso ao evento.
              </p>
              <p>
                Caso o benefício não seja comprovado, o portador deverá complementar o valor do ingresso adquirido para 
                o valor do ingresso integral, caso contrário o acesso ao evento não será permitido.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Rio Grande do Sul</h3>
            <div className={policyPageSectionContent}>
              <p>Estudantes, jovens de 15 a 29 anos pertencentes a famílias de baixa renda, idoso e deficiente e seu acompanhante.</p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Doadores regulares de sangue (registrados no hemocentro e nos bancos de sangue dos hospitais do Estado) 
                  – Lei Estadual RS nº 13.891/2012 (apresentar documento de identidade oficial com foto e carteira de 
                  controle das doações expedida pela Secretaria de Estado da Saúde ou pelos hemocentros e bancos de 
                  sangue do Estado do Rio Grande do Sul, dentro do prazo de validade).
                </li>
              </ul>
              <p style={{ marginTop: 'var(--spacing-md)' }}><strong>Exclusivamente para o município de Porto Alegre:</strong></p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Menores de 15 anos – Descontos nos termos das leis municipais 9989/2006 e 11.211/2012.
                </li>
                <li className={policyPageListItem}>
                  Aposentados ou Pensionistas do INSS (que recebam até três salários mínimos) – Lei Municipal de Porto 
                  Alegre nº 7.366/1993 (apresentar documento fornecido pela Federação dos Aposentados e Pensionistas do 
                  Estado do Rio Grande do Sul ou outras Associações de Classe devidamente registradas ou filiadas à 
                  citada Federação. Deve-se comprovar o preenchimento dos requisitos previstos na lei).
                </li>
              </ul>
            </div>
            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p style={{ marginBottom: 'var(--spacing-xs)' }}>
                Para Pontos de Venda e Bilheterias é necessária a comprovação do direito ao benefício da meia-entrada 
                no ato da compra e no acesso ao evento. Para vendas pela Internet e Telefone é necessária a comprovação 
                do direito ao benefício da meia-entrada no acesso ao evento.
              </p>
              <p>
                Caso o benefício não seja comprovado, o portador deverá complementar o valor do ingresso adquirido para 
                o valor do ingresso integral, caso contrário o acesso ao evento não será permitido.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Rio Grande do Norte</h3>
            <div className={policyPageSectionContent}>
              <p>Estudantes, jovens de 15 a 29 anos pertencentes a famílias de baixa renda, idoso e deficiente e seu acompanhante.</p>
              <p style={{ marginTop: 'var(--spacing-md)' }}><strong>Exclusivamente para o município de Natal:</strong></p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Menores de 7 anos – Lei Municipal de Natal nº6.503/93 e 4.743/96 (apresentar documento de identidade 
                  oficial com foto que comprove idade inferior a 7 anos).
                </li>
              </ul>
            </div>
            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p style={{ marginBottom: 'var(--spacing-xs)' }}>
                Para Pontos de Venda e Bilheterias é necessária a comprovação do direito ao benefício da meia-entrada 
                no ato da compra e no acesso ao evento. Para vendas pela Internet e Telefone é necessária a comprovação 
                do direito ao benefício da meia-entrada no acesso ao evento.
              </p>
              <p>
                Caso o benefício não seja comprovado, o portador deverá complementar o valor do ingresso adquirido para 
                o valor do ingresso integral, caso contrário o acesso ao evento não será permitido.
              </p>
            </div>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Paraná</h3>
            <div className={policyPageSectionContent}>
              <p>Estudantes, jovens de 15 a 29 anos pertencentes a famílias de baixa renda, idoso e deficiente e seu acompanhante.</p>
              <ul className={policyPageList}>
                <li className={policyPageListItem}>
                  Doadores regulares de sangue (registrados no hemocentro e nos bancos de sangue dos hospitais do Estado) 
                  – Lei Estadual PR nº 13.964/2002 (apresentar documento de identidade oficial com foto e documento 
                  oficial expedido pela Secretaria de Estado da Saúde que comprove a condição).
                </li>
                <li className={policyPageListItem}>
                  Professores da rede pública e particular Estadual – Lei Estadual PR nº 15.876/2008 (apresentar 
                  documento de identidade oficial com foto e comprovante de vínculo empregatício com a instituição de 
                  ensino que comprove a condição).
                </li>
              </ul>
            </div>
            <div className={policyPageAttention}>
              <p className={policyPageAttentionTitle}>Atenção:</p>
              <p style={{ marginBottom: 'var(--spacing-xs)' }}>
                Para Pontos de Venda e Bilheterias é necessária a comprovação do direito ao benefício da meia-entrada 
                no ato da compra e no acesso ao evento. Para vendas pela Internet e Telefone é necessária a comprovação 
                do direito ao benefício da meia-entrada no acesso ao evento.
              </p>
              <p>
                Caso o benefício não seja comprovado, o portador deverá complementar o valor do ingresso adquirido para 
                o valor do ingresso integral, caso contrário o acesso ao evento não será permitido.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HalfPricePolicy;
